import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Collapse,
  Flex,
  Icon,
  Select,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import FormGrid from "@raiden/library/components/Form/FormGrid";
import HoldersDietSelect from "@raiden/library/components/Form/HoldersDietSelet";
import PlacesAutocomplete from "@raiden/library/components/Form/PlacesAutocomplete";
import SchoolLevelSelect from "@raiden/library/components/Form/SchoolLevelsSelect";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import FormObserver from "@raiden/library/components/ReactHookForm/FormObserver";
import {
  HOLDERS_GENDERS_LIST,
  HOLDERS_TYPES_VALUE_CHILD,
} from "@raiden/library/constants/holders";
import { PLACES_TYPES_VALUE_SCHOOL } from "@raiden/library/constants/places";
import { usePreferences } from "@raiden/library/contexts/Preferences";
import { usePreferredYear } from "@raiden/library/hooks/usePreferredYear";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { IoAddOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import HoldersDialogCreate from "../../Holders/DialogCreate";
import WizardHolderCover from "./Cover";
import { getWizardHolderFormData } from "./Handler";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Holder").Holder[]} initialHolders
 * @property {import("react-hook-form").FieldArrayWithId<import("./Handler").WizardHoldersFormValues, "data", "id">[]} fields
 * @property {import("react-hook-form").UseFieldArrayAppend<import("./Handler").WizardHoldersFormValues, "data">} append
 *
 * @param {Props} props
 */
function Form({ initialHolders, fields, append }) {
  const intl = useIntl();

  const { isOpen, onOpen, onClose } = useDisclosure();

  /** @type {import("react-hook-form").UseFormReturn<import("./Handler").WizardHoldersFormValues>} */
  const { control, setValue } = useFormContext();

  /**
   * @param {number} index
   * @returns
   */
  function handlerClickEnable(index) {
    setValue(`data.${index}._change`, "update", { shouldDirty: true });
  }

  /**
   * @param {number} index
   * @returns
   */
  function handlerClickDisable(index) {
    setValue(`data.${index}._change`, "delete", { shouldDirty: true });
  }

  const [addedHolders, setAddedHolders] = useState(
    /** @type {import("@raiden/library/types/Holder").Holder[]} */ ([]),
  );

  /** @type {import("@raiden/library/types/Holder").Holder[]} */
  const holders = useMemo(() => {
    return [...initialHolders, ...addedHolders];
  }, [addedHolders, initialHolders]);

  const { bestEnvironment } = usePreferences();

  const preferredYear = usePreferredYear();

  return (
    <>
      <HoldersDialogCreate
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={({ holder }) => {
          setAddedHolders((holders) => [...holders, holder]);
          preferredYear &&
            append(
              getWizardHolderFormData({
                holder,
                preferredYear,
              }),
            );
        }}
      />

      <Stack spacing="1rem">
        <Flex justify="flex-end">
          <Button
            onClick={onOpen}
            leftIcon={<Icon as={IoAddOutline} />}
            colorScheme="brandPrimary">
            <FormattedMessage defaultMessage="Ajouter un adhérent" />
          </Button>
        </Flex>

        <Box>
          <ApiErrorMessage />

          <Stack spacing="1rem">
            {fields.map((field, index) => {
              const holder = holders.find(
                (holder) => holder.id === field.holder_id,
              );
              return (
                <Stack
                  alignItems="flex-start"
                  direction={{ base: "column", lg: "row" }}
                  borderWidth="1px"
                  borderRadius="0.375rem"
                  spacing="0"
                  key={field.id}>
                  {holder && <WizardHolderCover holder={holder} />}

                  <Box flexGrow={1} p="1rem">
                    {holder && (
                      <Text>
                        <FormattedMessage
                          defaultMessage="{firstName} {lastName}"
                          values={{
                            firstName: holder.first_name,
                            lastName: holder.last_name,
                          }}
                        />
                      </Text>
                    )}

                    <FormObserver
                      control={control}
                      name={`data.${index}._change`}>
                      {(change) => (
                        <>
                          <Collapse
                            in={change === "delete"}
                            initial={false}
                            unmountOnExit={true}>
                            <Box pt="1rem">
                              <Alert status="warning" borderRadius="0.375rem">
                                <AlertDescription>
                                  <FormattedMessage defaultMessage="Cet adhérent sera désactivé." />
                                </AlertDescription>
                              </Alert>
                            </Box>
                          </Collapse>

                          <Collapse
                            in={change === "update"}
                            initial={false}
                            unmountOnExit={true}>
                            <Stack spacing="1rem" pt="1rem">
                              <FormGrid>
                                <FormController
                                  name={`data.${index}.gender`}
                                  label={intl.formatMessage({
                                    defaultMessage: "Civilité",
                                  })}
                                  rules={{ required: true }}
                                  renderWithFormControl={(field) => (
                                    <Select {...field}>
                                      <option value="">-</option>

                                      {HOLDERS_GENDERS_LIST?.map?.(
                                        (gender, index) => (
                                          <option
                                            value={gender.value}
                                            key={gender.value}>
                                            {intl.formatMessage(
                                              gender.civility,
                                            )}
                                          </option>
                                        ),
                                      )}
                                    </Select>
                                  )}
                                />

                                <FormController
                                  control={control}
                                  name={`data.${index}.last_name`}
                                  label={intl.formatMessage({
                                    defaultMessage: "Nom",
                                  })}
                                />

                                <FormController
                                  control={control}
                                  name={`data.${index}.first_name`}
                                  label={intl.formatMessage({
                                    defaultMessage: "Prénom",
                                  })}
                                />

                                <FormController
                                  control={control}
                                  name={`data.${index}.birthdate`}
                                  type="date"
                                  label={intl.formatMessage({
                                    defaultMessage: "Date de naissance",
                                  })}
                                />
                              </FormGrid>

                              {holder?.type === HOLDERS_TYPES_VALUE_CHILD && (
                                <FormGrid>
                                  <FormController
                                    control={control}
                                    name={`data.${index}.diet`}
                                    label={intl.formatMessage({
                                      defaultMessage: "Régime alimentaire",
                                    })}
                                    renderWithFormControl={(field) => (
                                      <HoldersDietSelect
                                        control={control}
                                        {...field}
                                      />
                                    )}
                                  />
                                </FormGrid>
                              )}

                              {holder?.type === HOLDERS_TYPES_VALUE_CHILD ? (
                                <Stack spacing="1rem">
                                  <FormController
                                    name={`data.${index}.comment`}
                                    type="textarea"
                                    label={intl.formatMessage({
                                      defaultMessage:
                                        "Renseignements médicaux concernant l'enfant",
                                    })}
                                  />

                                  <FormGrid>
                                    <FormController
                                      name={`data.${index}.schooling.school_id`}
                                      rules={{
                                        required: true,
                                        onChange: (value) => {
                                          setValue(
                                            `data.${index}.schooling.level`,
                                            "",
                                          );
                                        },
                                      }}
                                      defaultValue=""
                                      label={intl.formatMessage({
                                        defaultMessage: "École",
                                      })}
                                      renderWithFormControl={(field) => (
                                        <PlacesAutocomplete
                                          type={[PLACES_TYPES_VALUE_SCHOOL]}
                                          {...field}
                                        />
                                      )}
                                    />

                                    <FormObserver
                                      name={`data.${index}.schooling.school_id`}>
                                      {(schoolId) => (
                                        <FormController
                                          name={`data.${index}.schooling.level`}
                                          rules={{
                                            required: true,
                                          }}
                                          defaultValue=""
                                          isDisabled={(schoolId ?? "") === ""}
                                          label={intl.formatMessage({
                                            defaultMessage: "Niveau",
                                          })}
                                          renderWithFormControl={(field) => (
                                            <SchoolLevelSelect
                                              {...field}
                                              schoolId={schoolId}
                                              environmentId={
                                                bestEnvironment?.id
                                                  ? String(bestEnvironment?.id)
                                                  : undefined
                                              }
                                            />
                                          )}
                                        />
                                      )}
                                    </FormObserver>
                                  </FormGrid>
                                </Stack>
                              ) : null}
                            </Stack>
                          </Collapse>
                        </>
                      )}
                    </FormObserver>

                    <Flex justify="flex-end" mt="1rem">
                      <FormObserver
                        control={control}
                        name={`data.${index}._change`}>
                        {(change) => (
                          <Button
                            onClick={() =>
                              change === "update"
                                ? handlerClickDisable(index)
                                : handlerClickEnable(index)
                            }
                            variant="outline"
                            colorScheme={change === "update" ? "red" : "green"}
                            size="sm">
                            {change === "update" ? (
                              <FormattedMessage defaultMessage="Désactiver cet adhérent" />
                            ) : (
                              <FormattedMessage defaultMessage="Réactiver cet adhérent" />
                            )}
                          </Button>
                        )}
                      </FormObserver>
                    </Flex>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Stack>
    </>
  );
}

export default Form;
